import { useEffect, useState } from "react";
import client, { downloadCamasirhaneFile } from "../../../api/camasirhaneApi";
import { MONTHS, YEARS } from "../utils/constants";
import { downloadDashboardExcelReport } from "../utils/dashboardExcelReport";
import { periodSummaryToExportData } from "../utils/periodExportData";
import { useAuth } from "../context/CamasirhaneRoleContext";
import { canWrite } from "../utils/auth";
import "../styles/shared.css";

const SOURCE_TYPES = [
  { value: "MONTHLY", label: "Aylık Tartım Raporu (Tüm Sayfalar)" },
  { value: "COST_REPORT", label: "Maliyet Raporu (çok aylı dosya)" },
  { value: "IRON_REPORT", label: "Demir Ölçüm Raporu (çok aylı dosya)" },
  { value: "WEIGHING", label: "Tartım (şablon)" },
  { value: "PRODUCTION", label: "Üretim (şablon)" },
  { value: "COST", label: "Maliyet (şablon)" },
  { value: "IRON", label: "Demir Ölçüm (şablon)" },
];

// Dönemi kendi sayfa adlarından çözen, ay/yıl seçimi gerektirmeyen tipler
const SELF_DATED = ["COST_REPORT", "IRON_REPORT"];

export default function ImportExportScreen() {
  const { user } = useAuth();
  const [sourceType, setSourceType] = useState("MONTHLY");
  const [year, setYear] = useState(2026);
  const [month, setMonth] = useState(7);
  const [file, setFile] = useState(null);
  const [batches, setBatches] = useState([]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const writable = canWrite(user);

  function loadBatches() {
    client.get("/imports/batches/").then((res) => setBatches(res.data)).catch(() => {});
  }

  useEffect(() => { loadBatches(); }, []);

  async function handleUpload(e) {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    setMessage("");
    setError("");
    const form = new FormData();
    form.append("file", file);
    form.append("source_type", sourceType);
    form.append("year", year);
    form.append("month", month);
    try {
      // FormData için Content-Type ELLE verilmez; tarayıcı boundary ile otomatik ekler.
      const res = await client.post("/imports/upload/", form);
      setMessage(`${res.data.saved} satır kaydedildi.${res.data.errors?.length ? ` ${res.data.errors.length} hata.` : ""}`);
      setFile(null);
      e.target.reset();
      loadBatches();
    } catch (err) {
      setError(err.response?.data?.detail || "Aktarım başarısız.");
    } finally {
      setUploading(false);
    }
  }

  function handleDownload(path, params, filename) {
    downloadCamasirhaneFile(path, params, filename).catch(() => setError("Dışa aktarma başarısız."));
  }

  async function handlePeriodExport() {
    setExporting(true);
    setError("");
    try {
      const res = await client.get("/reports/period-summary/", { params: { year, month } });
      const exportData = periodSummaryToExportData(res.data);
      await downloadDashboardExcelReport({
        periodLabel: `${MONTHS[month - 1]} ${year}`,
        year,
        months: [month],
        ...exportData,
        filename: `camasirhane_${year}_${month}.xlsx`,
      });
    } catch {
      setError("Dışa aktarma başarısız.");
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="screen">
      <div className="screen-header">
        <h2>Excel Aktarım / Dışa Aktarma</h2>
        <p>Geçmiş verileri içe aktarın veya raporları Excel olarak indirin</p>
      </div>

      {writable && (
        <div className="panel">
          <h2>Excel İçe Aktar</h2>
          <form onSubmit={handleUpload}>
            <div className="toolbar" style={{ marginBottom: 0, boxShadow: "none", border: "none", padding: 0 }}>
              <select className="ui-select" value={sourceType} onChange={(e) => setSourceType(e.target.value)}>
                {SOURCE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              {!SELF_DATED.includes(sourceType) && (
                <>
                  <select className="ui-select" value={month} onChange={(e) => setMonth(Number(e.target.value))} title="Ay">
                    {MONTHS.map((name, i) => <option key={name} value={i + 1}>{name}</option>)}
                  </select>
                  <select className="ui-select" value={year} onChange={(e) => setYear(Number(e.target.value))} title="Yıl">
                    {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </>
              )}
              <input type="file" accept=".xlsx,.xls" onChange={(e) => setFile(e.target.files?.[0] || null)} />
              {!["MONTHLY", ...SELF_DATED].includes(sourceType) && (
                <button type="button" className="ui-btn ui-btn--ghost" onClick={() => handleDownload("exports/template/", { source_type: sourceType }, `sablon_${sourceType.toLowerCase()}.xlsx`)}>
                  Şablon İndir
                </button>
              )}
              <button type="submit" className="ui-btn" disabled={!file || uploading}>
                {uploading ? "Aktarılıyor..." : "Yükle"}
              </button>
            </div>
          </form>
          {sourceType === "MONTHLY" && (
            <p style={{ fontSize: 13, color: "var(--color-text-muted)", marginTop: 12, marginBottom: 0 }}>
              Aylık raporun tamamı tek dosyadan aktarılır: her otel için <strong>Kirli / Temiz Tartım</strong> ve
              <strong> Üretim Adet</strong> sayfaları otomatik okunur. Otel, departman ve malzeme cinsleri yoksa
              otomatik oluşturulur. Yukarıdan dosyanın ait olduğu <strong>ay ve yılı</strong> seçmeyi unutmayın
              (dosyada ay/yıl bilgisi yer almadığı için buradan alınır). <em>GENEL</em> ve boş sayfalar atlanır.
            </p>
          )}
          {SELF_DATED.includes(sourceType) && (
            <p style={{ fontSize: 13, color: "var(--color-text-muted)", marginTop: 12, marginBottom: 0 }}>
              Bu dosyada <strong>her sayfa bir aydır</strong> (ör. &quot;HAZİRAN 2016&quot;). Dönem sayfa
              adından otomatik okunur, ay/yıl seçmenize gerek yok. Tüm aylar tek seferde aktarılır; ay/yıl
              içermeyen sayfalar (yıllık özet, boş sayfalar) atlanır.
            </p>
          )}
          {message && <p className="status-msg success" style={{ marginTop: 12 }}>{message}</p>}
          {error && <p className="status-msg error" style={{ marginTop: 12 }}>{error}</p>}
        </div>
      )}

      <div className="panel">
        <h2>Excel Dışa Aktar</h2>
        <div className="toolbar" style={{ marginBottom: 0, boxShadow: "none", border: "none", padding: 0 }}>
          <select className="ui-select" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
            {MONTHS.map((name, i) => <option key={name} value={i + 1}>{name}</option>)}
          </select>
          <select className="ui-select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button className="ui-btn" onClick={handlePeriodExport} disabled={exporting}>
            {exporting ? "Hazırlanıyor..." : "Dönem Raporunu İndir"}
          </button>
        </div>
      </div>

      {writable && (
        <div className="panel">
          <h2>Son Aktarımlar</h2>
          <div className="grid-wrapper">
            <table className="data-grid">
              <thead>
                <tr>
                  <th>Dosya</th>
                  <th>Tip</th>
                  <th>Durum</th>
                  <th>Hata</th>
                  <th>Tarih</th>
                </tr>
              </thead>
              <tbody>
                {batches.map((b) => (
                  <tr key={b.id}>
                    <td className="label-col">{b.file_name}</td>
                    <td>{b.source_type}</td>
                    <td>{b.status}</td>
                    <td>{b.error_count}</td>
                    <td>{new Date(b.imported_at).toLocaleString("tr-TR")}</td>
                  </tr>
                ))}
                {!batches.length && <tr><td colSpan={5}>Henüz aktarım yok.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
